import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// constellation
import { Anchor, Heading, Page, Paragraph } from '@zillow/constellation-10';

// components
import UnsubscribeError from 'app/shared/components/UnsubscribeError';

// actions
import { updateSubscription } from 'app/shared/flux/actions/MarketingActions';

// misc/utils
import { queryStringToQueryObject } from 'app/shared/utils/urlUtils';

// types
import type { State } from 'app/shared/types/State';
import type { MarketUpdateStatusErrorType, MarketUpdateStatusType } from 'app/shared/types/ApiResponseTypes';
import { logError } from '@zg-rentals/log-error';

const UnsubscribePage = () => {
  const queryString = useSelector((state: State) => state.router.location.search);
  const pathname = useSelector((state: State) => state.router.location.pathname);
  const unsubscribeResult = useSelector((state: State) => state.marketing.unsubscribeResult);
  const dispatch = useDispatch();
  const queryObj = queryStringToQueryObject(queryString);
  const { email, marketUpdateId } = queryObj;
  const [isSubscribed, setIsSubscribed] = useState(unsubscribeResult.success ? unsubscribeResult.isSubscribed : null);
  const [address, setAddress] = useState(unsubscribeResult.success ? unsubscribeResult.address : null);
  const [isErrored, setIsErrored] = useState(!unsubscribeResult.success);
  const handleUndoClick = () => {
    dispatch(updateSubscription({ email, enabled: !isSubscribed, marketUpdateId }))
      // @ts-ignore
      .then((res: MarketUpdateStatusType) => {
        const { marketUpdateEnabled, streetAddress } = res;
        const { street, state, city, zip, unit, hasUnitLabel } = streetAddress;
        const formattedAddress: string = `${street} ${hasUnitLabel ? unit : ''}, ${city}, ${state} ${zip}`;
        setIsSubscribed(marketUpdateEnabled);
        setAddress(formattedAddress);
        return;
      })
      .catch((err: MarketUpdateStatusErrorType) => {
        logError({
          error: err,
          errorType: 'Subscription',
          context: {
            details: 'Unsubscribe Page: Error updating subscription',
            root: 'pages/UnsubscribePage#handleUndoClick',
            pathAttempted: pathname,
            queryAttempted: queryString,
          },
        });
        setIsErrored(true);
      });
  };
  return (
    <Page.Root
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'bg.neutral',
      }}
    >
      <Page.Content css={{ paddingBottom: '1500' }}>
        {isErrored ? (
          <UnsubscribeError />
        ) : (
          <>
            <Heading level={1} textStyle="heading-lg" fontFamily="heading-alt" css={{ marginTop: '800' }}>
              Thank you!
            </Heading>
            <Heading level={2} textStyle="body-lg-bold" css={{ marginTop: '600' }}>
              You have been {isSubscribed ? 'subscribed to' : 'unsubscribed from'} the Rental Marketplace Update.
            </Heading>
            <Paragraph css={{ marginTop: '400' }}>
              Property: <strong>{address}</strong>
            </Paragraph>
            <Paragraph css={{ marginTop: '400' }}>
              Email address: <strong>{email}</strong>
            </Paragraph>
            <Paragraph css={{ marginTop: '400' }}>
              <Anchor onClick={handleUndoClick}>
                <strong>Undo</strong>
              </Anchor>
            </Paragraph>
          </>
        )}
      </Page.Content>
    </Page.Root>
  );
};

export default UnsubscribePage;
